import { useEffect, useState } from 'react';
import {
  createMetaCampaign,
  listMetaCampaigns,
  listMetaPagesByUser,
  adminListUsers,
  adminGetMetaToken,
  adminSaveMetaToken,
  updateMetaCampaignStatus,
} from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n/LangContext';
import AppNav from '../components/AppNav';

const OBJECTIVE_KEYS = [
  { value: 'OUTCOME_TRAFFIC',   label: 'Traffic' },
  { value: 'OUTCOME_AWARENESS', label: 'Brand Awareness' },
];

const STATUS_COLORS = {
  active: 'var(--mint)',
  paused: 'var(--yellow)',
  error:  'var(--red)',
};

function formatBudget(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MetaCampaignPage() {
  const { t } = useT();
  const navigate = useNavigate();

  // Token management
  const [tokenInfo, setTokenInfo]       = useState(null);
  const [newToken, setNewToken]         = useState('');
  const [tokenBusy, setTokenBusy]       = useState(false);
  const [tokenMsg, setTokenMsg]         = useState('');
  const [showTokenForm, setShowTokenForm] = useState(false);

  // Client selector
  const [users, setUsers]               = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Per-client data
  const [campaigns, setCampaigns]       = useState([]);
  const [pages, setPages]               = useState([]);
  const [loadError, setLoadError]       = useState('');
  const [clientLoading, setClientLoading] = useState(false);

  // UI state
  const [expandedId, setExpandedId]     = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [toast, setToast]               = useState('');
  const [toastType, setToastType]       = useState('ok');
  const [toastFade, setToastFade]       = useState(false);
  const [busy, setBusy]                 = useState(false);
  const [formError, setFormError]       = useState('');
  const [adCopyPreview, setAdCopyPreview] = useState(null);

  const [lang, setLang] = useState('de');

  const [form, setForm] = useState({
    pageId: '',
    name: '',
    objective: 'OUTCOME_TRAFFIC',
    dailyBudgetDollars: '10',
    status: 'PAUSED',
    testMode: false,
    targetCity: '',
  });

  // Load all users + token info on mount
  useEffect(() => {
    adminListUsers()
      .then((d) => setUsers(d.users || []))
      .catch((err) => setLoadError(err.message));
    adminGetMetaToken()
      .then((d) => setTokenInfo(d))
      .catch(() => {});
  }, []);

  async function onSaveToken(exchange) {
    if (!newToken.trim()) { setTokenMsg('Paste a token first.'); return; }
    setTokenBusy(true);
    setTokenMsg('');
    try {
      const res = await adminSaveMetaToken({ token: newToken.trim(), exchange });
      setTokenMsg(res.message);
      setNewToken('');
      setTokenInfo({ hasToken: true, preview: res.preview, source: 'database', expiresAt: res.expiresAt });
    } catch (err) {
      setTokenMsg(`Error: ${err.message}`);
    } finally {
      setTokenBusy(false);
    }
  }

  // Load campaigns + pages when a client is selected
  useEffect(() => {
    if (!selectedUserId) {
      setCampaigns([]);
      setPages([]);
      return;
    }
    setClientLoading(true);
    setLoadError('');
    Promise.all([
      listMetaCampaigns(selectedUserId),
      listMetaPagesByUser(selectedUserId),
    ])
      .then(([campData, pageData]) => {
        setCampaigns(campData.campaigns || []);
        setPages(pageData.pages || []);
        setShowForm(false);
        setAdCopyPreview(null);
        setForm((prev) => ({ ...prev, pageId: '', name: '' }));
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setClientLoading(false));
  }, [selectedUserId]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function showToastMsg(msg, type = 'ok') {
    setToast(msg);
    setToastType(type);
    setToastFade(false);
    setTimeout(() => setToastFade(true), 2400);
    setTimeout(() => { setToast(''); setToastFade(false); }, 3000);
  }

  function validateForm() {
    if (!selectedUserId) return 'Please select a client first.';
    if (!form.pageId)    return 'Please select a landing page.';
    if (!form.name.trim()) return 'Campaign name is required.';
    const budget = parseFloat(form.dailyBudgetDollars);
    if (isNaN(budget) || budget < 1) return 'Daily budget must be at least $1.';
    return null;
  }

  async function onToggleStatus(campaign) {
    const newStatus = campaign.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await updateMetaCampaignStatus({ id: campaign.id, status: newStatus });
      setCampaigns((prev) => prev.map((c) => c.id === campaign.id ? { ...c, status: newStatus } : c));
      showToastMsg(`Campaign ${newStatus === 'ACTIVE' ? 'activated' : 'paused'} on Facebook.`);
    } catch (err) {
      showToastMsg(`Error: ${err.message}`, 'err');
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setFormError('');
    setAdCopyPreview(null);
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setBusy(true);
    try {
      const result = await createMetaCampaign({
        pageId: form.pageId,
        targetUserId: selectedUserId,
        name: form.name.trim(),
        testMode: form.testMode,
        objective: form.objective,
        dailyBudgetCents: Math.round(parseFloat(form.dailyBudgetDollars) * 100),
        status: form.status,
        provider: 'openai',
        lang,
        ...(form.targetCity.trim() && { targetCity: form.targetCity.trim() }),
      });
      setCampaigns((prev) => [result.campaign, ...prev]);
      if (result.adCopy) setAdCopyPreview(result.adCopy);
      showToastMsg('Campaign created successfully!');
      setForm((prev) => ({ ...prev, name: '', pageId: '', targetCity: '' }));
    } catch (error) {
      setFormError(error.message);
      showToastMsg('Failed to create campaign.', 'err');
    } finally {
      setBusy(false);
    }
  }

  const activeCount  = campaigns.filter((c) => c.status === 'ACTIVE').length;
  const totalBudget  = campaigns.reduce((sum, c) => sum + (c.daily_budget_cents || 0), 0);
  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <main className="crm-page-wrap">
      <div className="grain" />

      {toast && (
        <div
          className={`crm-toast${toastFade ? ' fade-out' : ''}`}
          style={toastType === 'err' ? { borderColor: 'var(--red)', color: 'var(--red)' } : undefined}
        >
          {toast}
        </div>
      )}

      <AppNav
        activePage="meta"
        showLinks={false}
        rightSlot={(
          <button className="appnav-link" type="button" onClick={() => navigate('/admin')}>
            ← Back to Admin
          </button>
        )}
      />

      <div className="meta-shell">

        {/* ── META TOKEN MANAGEMENT ── */}
        <section className="crm-panel" style={{ marginBottom: '1.5rem' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setShowTokenForm((v) => !v)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setShowTokenForm((v) => !v)}
          >
            <h2 className="crm-title" style={{ marginBottom: 0 }}>
              🔑 Meta Access Token
              {tokenInfo && (() => {
                const daysLeft = tokenInfo.expiresAt
                  ? Math.ceil((new Date(tokenInfo.expiresAt) - Date.now()) / (1000 * 60 * 60 * 24))
                  : null;
                const expiryColor = daysLeft === null ? 'var(--muted)'
                  : daysLeft <= 7 ? '#ef4444'
                  : daysLeft <= 20 ? '#f59e0b'
                  : 'var(--mint)';
                return (
                  <span style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: '12px' }}>
                    {tokenInfo.hasToken ? (
                      <>
                        <span style={{ color: 'var(--mint)' }}>✓ Active — {tokenInfo.preview}</span>
                        {daysLeft !== null && (
                          <span style={{ marginLeft: '10px', color: expiryColor, fontWeight: 600 }}>
                            {daysLeft > 0 ? `⏱ ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left` : '⚠ Expired'}
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--red)' }}>✗ No token set</span>
                    )}
                  </span>
                );
              })()}
            </h2>
            <span className="meta-chevron">{showTokenForm ? '▾' : '▸'}</span>
          </div>

          {showTokenForm && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                Short-lived tokens expire in ~1 hour. Paste one below and click <strong>Exchange → Save</strong> to convert it to a 60-day long-lived token automatically.<br />
                For a permanent token: use a <strong>System User</strong> in Meta Business Manager → System Users → Generate Token.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <input
                  className="meta-input"
                  style={{ flex: 1, minWidth: '260px', fontFamily: 'monospace', fontSize: '0.8rem' }}
                  placeholder="Paste Facebook access token here…"
                  value={newToken}
                  onChange={(e) => setNewToken(e.target.value)}
                />
                <button
                  className="meta-submit-btn"
                  type="button"
                  disabled={tokenBusy}
                  onClick={() => onSaveToken(true)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {tokenBusy ? '…' : 'Exchange → Save (60 days)'}
                </button>
                <button
                  className="meta-submit-btn"
                  type="button"
                  disabled={tokenBusy}
                  onClick={() => onSaveToken(false)}
                  style={{ whiteSpace: 'nowrap', background: 'var(--surface)', border: '1px solid var(--border)' }}
                >
                  {tokenBusy ? '…' : 'Save as-is'}
                </button>
              </div>
              {tokenMsg && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: tokenMsg.startsWith('Error') ? 'var(--red)' : 'var(--mint)' }}>
                  {tokenMsg}
                </p>
              )}
            </div>
          )}
        </section>

        {/* ── CLIENT SELECTOR ── */}
        <section className="crm-panel" style={{ marginBottom: '1.5rem' }}>
          <h2 className="crm-title" style={{ marginBottom: '1rem' }}>Select Client</h2>
          {loadError && <p className="meta-error">{loadError}</p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <select
              className="meta-select"
              style={{ minWidth: '280px', maxWidth: '420px' }}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">— Choose a client —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.email}</option>
              ))}
            </select>
            {selectedUser && (
              <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                {pages.length} published page{pages.length !== 1 ? 's' : ''} · {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
              </span>
            )}
            {clientLoading && <span style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>Loading…</span>}
          </div>
        </section>

        {selectedUserId && (
          <>
            {/* ── STATS ── */}
            <div className="meta-stats-strip">
              <div className="meta-stat-card">
                <span className="meta-stat-label">Total Campaigns</span>
                <span className="meta-stat-value">{campaigns.length}</span>
              </div>
              <div className="meta-stat-card">
                <span className="meta-stat-label">Active Now</span>
                <span className="meta-stat-value" style={{ color: 'var(--mint)' }}>{activeCount}</span>
              </div>
              <div className="meta-stat-card">
                <span className="meta-stat-label">Combined Daily Budget</span>
                <span className="meta-stat-value">{formatBudget(totalBudget)}</span>
              </div>
              <div className="meta-stat-card">
                <span className="meta-stat-label">Published Pages</span>
                <span className="meta-stat-value">{pages.length}</span>
              </div>
            </div>

            {/* ── CREATE CAMPAIGN ── */}
            <section className="crm-panel meta-create-section">
              <div
                className="meta-section-header"
                onClick={() => setShowForm((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setShowForm((v) => !v)}
              >
                <h2 className="crm-title" style={{ marginBottom: 0 }}>
                  <span className="meta-icon">+</span> New Campaign for {selectedUser?.email}
                </h2>
                <span className="meta-chevron">{showForm ? '▾' : '▸'}</span>
              </div>

              {showForm && (
                <form onSubmit={onSubmit} className="meta-form" autoComplete="off">

                  {/* Landing Page */}
                  <div className="meta-form-row two-col">
                    <label className="meta-label">
                      <span className="meta-label-text">Landing Page</span>
                      {pages.length > 0 ? (
                        <select
                          className="meta-select"
                          value={form.pageId}
                          onChange={(e) => setField('pageId', e.target.value)}
                        >
                          <option value="">— Select page —</option>
                          {pages.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.description ? p.description.slice(0, 60) : p.id.slice(0, 8)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="meta-no-pages-notice">
                          <span style={{ fontSize: '20px' }}>⏳</span>
                          <span>This client has no published pages yet.</span>
                        </div>
                      )}
                    </label>

                    <label className="meta-label">
                      <span className="meta-label-text">Campaign Name</span>
                      <input
                        className="meta-input"
                        placeholder="e.g. Spring Hiring – Frontend Dev"
                        value={form.name}
                        onChange={(e) => setField('name', e.target.value)}
                        autoComplete="off"
                      />
                    </label>
                  </div>

                  {/* Target City */}
                  <div className="meta-form-row">
                    <label className="meta-label">
                      <span className="meta-label-text">
                        Target City <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: '12px' }}>(optional — ads shown within 25 km radius)</span>
                      </span>
                      <input
                        className="meta-input"
                        placeholder="e.g. Karlsruhe, Berlin, München"
                        value={form.targetCity}
                        onChange={(e) => setField('targetCity', e.target.value)}
                        autoComplete="off"
                      />
                    </label>
                  </div>

                  {/* Ad Language toggle */}
                  <div className="meta-form-row">
                    <label className="meta-label">
                      <span className="meta-label-text">Ad Language</span>
                      <div style={{ display: 'flex', gap: '8px', maxWidth: '260px' }}>
                        {['de', 'en'].map((l) => (
                          <button
                            key={l}
                            type="button"
                            onClick={() => setLang(l)}
                            style={{
                              flex: 1, padding: '8px', borderRadius: '8px', fontWeight: 700,
                              fontSize: '0.85rem', cursor: 'pointer', border: '1px solid',
                              borderColor: lang === l ? 'var(--mint)' : 'var(--border)',
                              background: lang === l ? 'rgba(198,255,62,0.12)' : 'var(--surface)',
                              color: lang === l ? 'var(--mint)' : 'var(--muted)',
                            }}
                          >
                            {l === 'de' ? '🇩🇪 Deutsch' : '🇬🇧 English'}
                          </button>
                        ))}
                      </div>
                    </label>
                  </div>

                  {/* Settings */}
                  <div className="meta-form-row three-col">
                    <label className="meta-label">
                      <span className="meta-label-text">Objective</span>
                      <select className="meta-select" value={form.objective} onChange={(e) => setField('objective', e.target.value)}>
                        {OBJECTIVE_KEYS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="meta-label">
                      <span className="meta-label-text">Daily Budget</span>
                      <div className="meta-input-with-prefix">
                        <span className="meta-input-prefix">$</span>
                        <input
                          className="meta-input"
                          type="number"
                          min="1"
                          step="0.01"
                          value={form.dailyBudgetDollars}
                          onChange={(e) => setField('dailyBudgetDollars', e.target.value)}
                          required
                        />
                      </div>
                    </label>
                    <label className="meta-label">
                      <span className="meta-label-text">Launch Status</span>
                      <select className="meta-select" value={form.status} onChange={(e) => setField('status', e.target.value)}>
                        <option value="PAUSED">Paused</option>
                        <option value="ACTIVE">Active</option>
                      </select>
                    </label>
                  </div>

                  {formError && <p className="meta-error">{formError}</p>}

                  <button className="meta-submit-btn" type="submit" disabled={busy || pages.length === 0}>
                    {busy ? <><span className="meta-spinner" /> Creating on Meta…</> : 'Launch Campaign'}
                  </button>
                </form>
              )}

              {/* ── LIVE AD PREVIEW — always visible when form is open, updates with lang toggle ── */}
              {showForm && (() => {
                const previewPage = pages.find((p) => p.id === form.pageId);
                const pageDesc    = previewPage?.description || '';
                const isDE        = lang === 'de';

                // Company: use page.company field if present, else smart placeholder
                const company = previewPage?.company
                  || pageDesc.match(/(?:company|unternehmen|firma)\s*[:\-]\s*([^\n,.(]{2,40})/i)?.[1]?.trim()
                  || (isDE ? 'Ihr Unternehmen' : 'Your Company');
                const initials = company.split(/\s+/).map((w) => w[0]).join('').toUpperCase().slice(0, 2) || 'CO';

                // Job title: strip accidental "this role:" prefix, fall back to placeholder
                const rawName  = form.name.trim().replace(/^(?:this\s+)?role\s*[:\-]\s*/i, '').trim();
                const jobTitle = rawName
                  || pageDesc.match(/(?:suchen|seeking|looking for|für die stelle|position of)\s+(?:einen?\s+)?([^\n,.(]{3,50})/i)?.[1]?.trim()
                  || (isDE ? 'Offene Stelle' : 'Open Position');

                // Copy: AI values after creation, sensible placeholders before
                const headline    = adCopyPreview?.headline    || (isDE ? 'Jetzt bewerben – Stelle frei!' : "We're Hiring — Apply Now!");
                const primaryText = adCopyPreview?.primaryText || (pageDesc ? pageDesc.slice(0, 125) : (isDE ? 'Spannende Stelle wartet auf Sie. Jetzt bewerben!' : 'Exciting opportunity awaits. Apply today.'));
                const description = adCopyPreview?.description || (isDE ? 'Details ansehen & bewerben' : 'See full details & apply');
                const isAI        = !!adCopyPreview;

                return (
                  <div style={{ marginTop: '2rem', borderTop: '1px solid var(--border)', paddingTop: '1.5rem' }}>

                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: isAI ? 'var(--mint)' : 'var(--muted)', background: isAI ? 'rgba(198,255,62,0.1)' : 'rgba(255,255,255,0.05)', border: `1px solid ${isAI ? 'var(--mint)' : 'var(--border)'}`, borderRadius: '20px', padding: '3px 12px' }}>
                          {isAI ? (isDE ? '✓ KI-Anzeigentext' : '✓ AI Ad Copy') : (isDE ? '👁 Live-Vorschau' : '👁 Live Preview')}
                        </span>
                        {!isAI && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                            {isDE ? 'Echte KI-Texte erscheinen nach dem Start' : 'Real AI copy appears after launch'}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 600 }}>
                        <span style={{ color: '#1877f2' }}>▣ Facebook</span>
                        <span style={{ color: '#e1306c' }}>◈ Instagram</span>
                      </div>
                    </div>

                    {/* Extract salary from description for image panel */}
                    {(() => {
                      const salary = pageDesc.match(/(?:€|USD|EUR)\s?[\d.,k\s\-–]+(?:k)?(?:\s?\/\s?(?:Jahr|year|month|Monat|yr))?/i)?.[0]?.trim() || previewPage?.salary_range || '';
                      const igHandle = company.toLowerCase().replace(/[^a-z0-9]/g, '');

                      return (
                        <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

                          {/* ════════════ FACEBOOK FEED AD ════════════ */}
                          <div style={{ width: '320px', flexShrink: 0, border: '1px solid #cfd4da', borderRadius: '10px', overflow: 'hidden', fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif', background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.14)' }}>

                            {/* FB nav bar (mimics mobile top bar) */}
                            <div style={{ background: '#fff', borderBottom: '1px solid #e4e6eb', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 900, fontSize: '18px', color: '#1877f2', letterSpacing: '-1px' }}>facebook</span>
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#65676b', background: '#f0f2f5', borderRadius: '12px', padding: '2px 8px' }}>{isDE ? 'Anzeige' : 'Ad'}</span>
                            </div>

                            {/* Post header */}
                            <div style={{ padding: '10px 12px 6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'linear-gradient(135deg,#1a1a2e,#0f3460)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c6ff3e', fontWeight: 900, fontSize: '14px', flexShrink: 0 }}>{initials}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#050505', lineHeight: 1.2 }}>{company}</div>
                                <div style={{ fontSize: '11px', color: '#65676b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ color: '#1877f2', fontWeight: 600 }}>{isDE ? 'Gesponsert' : 'Sponsored'}</span>
                                  <span>·</span>
                                  <span>🌐</span>
                                </div>
                              </div>
                              <span style={{ fontSize: '20px', color: '#65676b', cursor: 'default', lineHeight: 1 }}>···</span>
                            </div>

                            {/* Primary text */}
                            <div style={{ padding: '0 12px 10px', fontSize: '14px', color: '#050505', lineHeight: 1.45 }}>
                              {primaryText.slice(0, 125)}
                              {primaryText.length > 125 && <><span style={{ color: '#65676b' }}>… </span><span style={{ color: '#1877f2', cursor: 'default' }}>{isDE ? 'Mehr' : 'See more'}</span></>}
                            </div>

                            {/* 16:9 image creative */}
                            <div style={{ position: 'relative', width: '100%', paddingTop: '52.5%', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#0d1b2a 0%,#1b2a4a 40%,#0f3460 75%,#1a4a6e 100%)' }}>
                                {/* Accent circles */}
                                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(198,255,62,0.08)' }} />
                                <div style={{ position: 'absolute', bottom: '-15px', left: '10%', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(198,255,62,0.06)' }} />
                                {/* Left accent bar */}
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: 'linear-gradient(to bottom,#c6ff3e,rgba(198,255,62,0.2))' }} />
                                {/* Content */}
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 20px' }}>
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#c6ff3e' }} />
                                    <span style={{ fontSize: '9px', fontWeight: 800, color: '#c6ff3e', letterSpacing: '2.5px', textTransform: 'uppercase' }}>
                                      {isDE ? 'Stelle verfügbar' : 'Now Hiring'}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '22px', fontWeight: 900, color: '#fff', lineHeight: 1.15, marginBottom: '8px', textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
                                    {jobTitle}
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {form.targetCity && (
                                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', padding: '2px 8px' }}>📍 {form.targetCity}</span>
                                    )}
                                    {salary && (
                                      <span style={{ fontSize: '11px', color: '#c6ff3e', background: 'rgba(198,255,62,0.12)', borderRadius: '4px', padding: '2px 8px', fontWeight: 700 }}>💰 {salary}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Link preview */}
                            <div style={{ background: '#f0f2f5', borderTop: '1px solid #cfd4da', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '10px', color: '#8a8d91', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' }}>FORMLYHR.COM</div>
                                <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#050505', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{headline}</div>
                                <div style={{ fontSize: '12px', color: '#65676b', marginTop: '2px' }}>{description}</div>
                              </div>
                              <button type="button" style={{ background: '#e4e6eb', border: 'none', borderRadius: '6px', padding: '9px 14px', fontSize: '13.5px', fontWeight: 700, cursor: 'default', whiteSpace: 'nowrap', color: '#050505', flexShrink: 0 }}>
                                {isDE ? 'Mehr erfahren' : 'Learn More'}
                              </button>
                            </div>

                            {/* Reactions row */}
                            <div style={{ padding: '5px 12px 0', borderTop: '1px solid #e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                {['👍','❤️','😮'].map((e) => <span key={e} style={{ fontSize: '13px' }}>{e}</span>)}
                                <span style={{ fontSize: '12px', color: '#65676b', marginLeft: '4px' }}>47</span>
                              </div>
                              <span style={{ fontSize: '12px', color: '#65676b' }}>3 {isDE ? 'Kommentare' : 'comments'}</span>
                            </div>
                            <div style={{ display: 'flex', borderTop: '1px solid #e4e6eb', margin: '4px 0 0' }}>
                              {[
                                { icon: '👍', label: isDE ? 'Gefällt mir' : 'Like' },
                                { icon: '💬', label: isDE ? 'Kommentieren' : 'Comment' },
                                { icon: '↗', label: isDE ? 'Teilen' : 'Share' },
                              ].map(({ icon, label }) => (
                                <button key={label} type="button" style={{ flex: 1, background: 'none', border: 'none', padding: '8px 0', fontSize: '12px', color: '#65676b', fontWeight: 600, cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                  <span>{icon}</span><span>{label}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* ════════════ INSTAGRAM FEED AD ════════════ */}
                          <div style={{ width: '300px', flexShrink: 0, border: '1px solid #dbdbdb', borderRadius: '10px', overflow: 'hidden', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', background: '#fafafa', boxShadow: '0 1px 6px rgba(0,0,0,0.12)' }}>

                            {/* IG top bar */}
                            <div style={{ background: '#fff', borderBottom: '1px solid #efefef', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 700, fontSize: '14px', fontStyle: 'italic', color: '#262626', letterSpacing: '-0.5px' }}>Instagram</span>
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#8e8e8e', background: '#f3f3f3', borderRadius: '12px', padding: '2px 8px' }}>{isDE ? 'Werbeanzeige' : 'Sponsored'}</span>
                            </div>

                            {/* IG post header */}
                            <div style={{ padding: '10px 12px', background: '#fff', display: 'flex', alignItems: 'center', gap: '9px' }}>
                              <div style={{ width: '36px', height: '36px', borderRadius: '50%', padding: '2px', background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', flexShrink: 0 }}>
                                <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg,#0f3460,#533483)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: '10px' }}>{initials}</div>
                                </div>
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#262626' }}>{igHandle}</div>
                                <div style={{ fontSize: '10.5px', color: '#8e8e8e' }}>{form.targetCity || (isDE ? 'Deutschland' : 'Germany')}</div>
                              </div>
                              <span style={{ fontSize: '18px', color: '#262626', cursor: 'default' }}>···</span>
                            </div>

                            {/* SQUARE 1:1 image */}
                            <div style={{ position: 'relative', width: '100%', paddingTop: '100%', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg,#0d1b2a 0%,#16213e 35%,#3d0c4e 70%,#1a1035 100%)' }}>
                                {/* Glow effects */}
                                <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '160px', height: '160px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(220,39,67,0.25),transparent 70%)' }} />
                                <div style={{ position: 'absolute', bottom: '10%', left: '-20px', width: '120px', height: '120px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(252,176,69,0.2),transparent 70%)' }} />
                                {/* Content */}
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '28px 24px' }}>
                                  {/* Badge */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                                    <div style={{ height: '1px', width: '20px', background: 'rgba(252,176,69,0.5)' }} />
                                    <span style={{ fontSize: '9px', fontWeight: 900, color: '#fcb045', letterSpacing: '3px', textTransform: 'uppercase' }}>
                                      {isDE ? 'Jetzt bewerben' : 'Now Hiring'}
                                    </span>
                                    <div style={{ height: '1px', width: '20px', background: 'rgba(252,176,69,0.5)' }} />
                                  </div>
                                  {/* Job title — larger for IG square */}
                                  <div style={{ fontSize: '28px', fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: '14px', textShadow: '0 2px 16px rgba(0,0,0,0.6)', maxWidth: '220px' }}>
                                    {jobTitle}
                                  </div>
                                  {/* Divider */}
                                  <div style={{ width: '32px', height: '2px', background: 'linear-gradient(90deg,transparent,#fcb045,transparent)', marginBottom: '12px' }} />
                                  {/* Details row */}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                                    {form.targetCity && <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>📍 {form.targetCity}</span>}
                                    {salary && <span style={{ fontSize: '12px', color: '#fcb045', fontWeight: 700 }}>💰 {salary}</span>}
                                  </div>
                                </div>
                                {/* Bottom CTA bar */}
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 14px', background: 'linear-gradient(to top,rgba(0,0,0,0.75) 0%,transparent 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>formlyhr.com</span>
                                  <div style={{ background: '#0095f6', color: '#fff', borderRadius: '20px', padding: '5px 16px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.2px' }}>
                                    {isDE ? 'Bewerben →' : 'Apply Now →'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* IG action row */}
                            <div style={{ background: '#fff', padding: '8px 12px 5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', gap: '14px' }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="1.8" style={{ cursor: 'default' }}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="1.8" style={{ cursor: 'default' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="1.8" style={{ cursor: 'default' }}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                              </div>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="1.8" style={{ cursor: 'default' }}><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                            </div>

                            {/* IG caption area */}
                            <div style={{ background: '#fff', padding: '0 12px 10px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#262626', marginBottom: '2px' }}>
                                {isDE ? '124 Gefällt-mir-Angaben' : '124 likes'}
                              </div>
                              <div style={{ fontSize: '13px', color: '#262626', lineHeight: 1.45, marginBottom: '4px' }}>
                                <strong>{igHandle}</strong>{' '}
                                {primaryText.slice(0, 72)}{primaryText.length > 72 && <><span style={{ color: '#8e8e8e' }}>… </span><span style={{ color: '#8e8e8e', cursor: 'default' }}>{isDE ? 'mehr' : 'more'}</span></>}
                              </div>
                              <div style={{ fontSize: '11.5px', color: '#8e8e8e', cursor: 'default' }}>
                                {isDE ? 'Kommentar hinzufügen …' : 'Add a comment…'}
                              </div>
                              <div style={{ fontSize: '10px', color: '#c7c7c7', textTransform: 'uppercase', letterSpacing: '0.3px', marginTop: '4px' }}>
                                {isDE ? 'Vor 2 Stunden' : '2 hours ago'}
                              </div>
                            </div>
                          </div>

                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </section>

            {/* ── CAMPAIGNS LIST ── */}
            <section className="crm-panel">
              <h2 className="crm-title">Campaign History — {selectedUser?.email}</h2>

              {campaigns.length === 0 ? (
                <p className="crm-empty">No campaigns yet for this client.</p>
              ) : (
                <div className="meta-campaign-list">
                  {campaigns.map((c) => {
                    const isExpanded  = expandedId === c.id;
                    const statusColor = STATUS_COLORS[(c.status || '').toLowerCase()] || 'var(--muted)';
                    return (
                      <div
                        key={c.id}
                        className={`meta-campaign-card${isExpanded ? ' expanded' : ''}`}
                        onClick={() => setExpandedId(isExpanded ? null : c.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setExpandedId(isExpanded ? null : c.id)}
                      >
                        <div className="meta-campaign-row">
                          <div className="meta-campaign-name">{c.name}</div>
                          <div className="meta-campaign-meta-pills">
                            <span className="meta-pill" style={{ borderColor: statusColor, color: statusColor }}>{c.status}</span>
                            <span className="meta-pill">{OBJECTIVE_KEYS.find((o) => o.value === c.objective)?.label || c.objective}</span>
                            <span className="meta-pill">{formatBudget(c.daily_budget_cents)}/day</span>
                          </div>
                          <div className="meta-campaign-date">{formatDate(c.created_at)}</div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onToggleStatus(c); }}
                            style={{
                              padding: '4px 14px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700,
                              border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                              background: c.status === 'ACTIVE' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                              color: c.status === 'ACTIVE' ? '#ef4444' : '#10b981',
                            }}
                          >
                            {c.status === 'ACTIVE' ? '⏸ Pause' : '▶ Activate'}
                          </button>
                        </div>

                        {isExpanded && (
                          <div className="meta-campaign-details">
                            <div className="meta-detail-grid">
                              <div className="meta-detail-item">
                                <span className="meta-detail-key">Campaign ID</span>
                                <code className="meta-detail-code">{c.meta_campaign_id || '—'}</code>
                              </div>
                              <div className="meta-detail-item">
                                <span className="meta-detail-key">Ad Set ID</span>
                                <code className="meta-detail-code">{c.meta_adset_id || '—'}</code>
                              </div>
                              <div className="meta-detail-item">
                                <span className="meta-detail-key">Ad ID</span>
                                <code className="meta-detail-code">{c.meta_ad_id || '—'}</code>
                              </div>
                              <div className="meta-detail-item">
                                <span className="meta-detail-key">Ad Account</span>
                                <code className="meta-detail-code">{c.ad_account_id || '—'}</code>
                              </div>
                              {c.page_description && (
                                <div className="meta-detail-item" style={{ gridColumn: '1/-1' }}>
                                  <span className="meta-detail-key">Page</span>
                                  <span style={{ fontSize: '0.82rem' }}>{c.page_description.slice(0, 120)}</span>
                                </div>
                              )}
                            </div>
                            {c.ad_copy && (
                              <div className="meta-adcopy-inline">
                                <span className="meta-detail-key">Ad Copy</span>
                                <div className="meta-adcopy-mini">
                                  <span><strong>Headline:</strong> {c.ad_copy.headline}</span>
                                  <span><strong>Primary text:</strong> {c.ad_copy.primaryText}</span>
                                  <span><strong>Description:</strong> {c.ad_copy.description}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {!selectedUserId && !loadError && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: 'var(--muted)', fontSize: '0.95rem' }}>
            Select a client above to view and manage their campaigns.
          </div>
        )}

      </div>
    </main>
  );
}
